import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Layers, Network, HardDrive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Panel } from '@/components/dashboard/Panel';
import { CapacityPanel } from '@/components/endpoints/CapacityPanel';
import { TlsPanel } from '@/components/endpoints/TlsPanel';
import { TraefikPanel } from '@/components/endpoints/TraefikPanel';
import { api, ApiError } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { Endpoint } from '@/types/endpoint';

const focusRing = 'rounded outline-none focus-visible:ring-2 focus-visible:ring-ring';

const backLink = (
  <Link to="/resources/endpoints" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
    Back to endpoints
  </Link>
);

export function EndpointDetailPage() {
  const { endpointId } = useParams<{ endpointId: string }>();
  // Keyed so that navigating between endpoints remounts every panel and its polling.
  return <EndpointDetail key={endpointId} rawId={endpointId} />;
}

function When({ iso }: { iso: string | undefined }) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return <span className="text-muted-foreground">Unknown</span>;
  return (
    <span>
      <time dateTime={new Date(t).toISOString()}>{formatAgo(t)}</time>
      <span className="text-muted-foreground"> ({new Date(t).toLocaleString()})</span>
    </span>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

const notSet = <span className="text-muted-foreground">Not set</span>;

const RESOURCE_LINKS = [
  { path: 'containers', label: 'Containers', text: 'Containers running on this endpoint', icon: Box },
  { path: 'stacks', label: 'Stacks', text: 'Stacks deployed on this endpoint', icon: Layers },
  { path: 'volumes', label: 'Volumes', text: 'Docker volumes on this endpoint', icon: HardDrive },
  { path: 'networks', label: 'Networks', text: 'Docker networks on this endpoint', icon: Network },
] as const;

function EndpointDetail({ rawId }: { rawId: string | undefined }) {
  const id = rawId !== undefined && /^\d+$/.test(rawId) && Number(rawId) > 0 ? Number(rawId) : null;
  // Set from the fetcher's async catch path; cleared by the next successful fetch.
  const [notFound, setNotFound] = useState(false);
  const { data: endpoint, error, loading, refetch } = usePolledData<Endpoint | null>(
    async () => {
      if (id === null) return null;
      try {
        const ep = await api.endpoints.get(id);
        setNotFound(false);
        return ep;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.code === 'NOT_FOUND')) setNotFound(true);
        throw e;
      }
    },
    id !== null && !notFound ? 30_000 : 0,
  );

  // Not found wins even when stale data is loaded (endpoint removed while the page was open).
  if (id === null || notFound) {
    return <EmptyState title="Endpoint not found" description="This endpoint does not exist or was removed." action={backLink} />;
  }

  if (!endpoint) {
    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      );
    }
    return (
      <EmptyState
        title="Could not load endpoint"
        description={error ?? undefined}
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refetch}>
              Retry
            </Button>
            {backLink}
          </div>
        }
      />
    );
  }

  const instanceName = endpoint.instance?.name;

  return (
    <div className="space-y-6">
      <PageHeader
        title={endpoint.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={endpoint.status} />
            <span>
              Docker endpoint
              {instanceName ? (
                <>
                  {' on '}
                  <Link to="/instances" className={`${focusRing} underline underline-offset-4`}>
                    {instanceName}
                  </Link>
                </>
              ) : null}
            </span>
          </span>
        }
        actions={backLink}
      />

      {error !== null ? (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Overview" loading={false} error={null} hasData>
          <dl className="space-y-2">
            <Row label="ID">{endpoint.id}</Row>
            <Row label="Type">{endpoint.type}</Row>
            <Row label="URL">{endpoint.url ? <span className="font-mono text-xs">{endpoint.url}</span> : notSet}</Row>
            <Row label="Public IP">{endpoint.public_ip ? <span className="font-mono text-xs">{endpoint.public_ip}</span> : notSet}</Row>
            <Row label="Created">
              <When iso={endpoint.created_at} />
            </Row>
            <Row label="Updated">
              <When iso={endpoint.updated_at} />
            </Row>
          </dl>
        </Panel>

        <Panel title="Resources on this endpoint" loading={false} error={null} hasData>
          <ul className="space-y-1">
            {RESOURCE_LINKS.map(({ path, label, text, icon: Icon }) => (
              <li key={path}>
                <Link
                  to={`/resources/${path}?endpoint_id=${endpoint.id}`}
                  className={`${focusRing} flex items-center gap-3 px-2 py-1.5 hover:bg-muted`}
                >
                  <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="text-sm">
                    <span className="font-medium">{label}</span>
                    <span className="text-muted-foreground"> — {text}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <CapacityPanel endpointId={endpoint.id} />
      <TraefikPanel endpoint={endpoint} />
      <TlsPanel endpointId={endpoint.id} />
    </div>
  );
}
